import React, { useState, useEffect } from 'react';
import { Badge, Button, Tooltip } from 'reactstrap';
import { useSocketConnection } from '../hooks/useSocketConnection';

const SocketConnectionStatus = ({ showDetails = false, className = '' }) => {
  const {
    isConnected,
    isConnecting,
    reconnectAttempts,
    connectionError,
    manualReconnect
  } = useSocketConnection();

  const [showTooltip, setShowTooltip] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Track last error for display
  useEffect(() => {
    if (connectionError) {
      setLastError(connectionError);
    }
  }, [connectionError]);

  // Auto-hide tooltip after 5 seconds
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => setShowTooltip(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  const getStatusColor = () => {
    if (isConnected) return 'success';
    if (isConnecting) return 'warning';
    if (connectionError) return 'danger';
    return 'secondary';
  };

  const getStatusText = () => {
    if (isConnected) return 'Connected';
    if (isConnecting) return 'Connecting...';
    if (connectionError) return 'Disconnected';
    return 'Unknown';
  };

  const getStatusIcon = () => {
    if (isConnected) return '🔗';
    if (isConnecting) return '🔄';
    if (connectionError) return '❌';
    return '❓';
  };

  const handleManualReconnect = () => {
    manualReconnect();
    setShowTooltip(true);
  };

  const getTooltipContent = () => {
    if (isConnected) {
      return 'Socket connection is active and working properly';
    }
    
    if (isConnecting) {
      return `Attempting to reconnect... (Attempt ${reconnectAttempts})`;
    }
    
    if (connectionError) {
      return `Connection error: ${lastError || 'Unknown error'}. Click to manually reconnect.`;
    }
    
    return 'Connection status unknown';
  };

  return (
    <div className={`socket-connection-status ${className}`}>
      <div className="d-flex align-items-center">
        <Badge 
          id="socket-status-badge"
          color={getStatusColor()} 
          className="me-2"
          style={{ 
            fontSize: '0.75rem',
            padding: '0.25rem 0.5rem',
            cursor: connectionError ? 'pointer' : 'default'
          }}
          onClick={connectionError ? handleManualReconnect : undefined}
        >
          <span className="me-1">{getStatusIcon()}</span>
          {getStatusText()}
        </Badge>
        
        {connectionError && (
          <Button
            size="sm"
            color="outline-primary"
            onClick={handleManualReconnect}
            disabled={isConnecting}
            className="btn-sm"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {isConnecting ? 'Reconnecting...' : 'Reconnect'}
          </Button>
        )}
      </div>

      {showDetails && (
        <div className="mt-2" style={{ fontSize: '0.75rem', color: '#6c757d' }}>
          {isConnecting && (
            <div>Reconnection attempts: {reconnectAttempts}</div>
          )}
          {connectionError && (
            <div className="text-danger">
              Error: {lastError || 'Unknown connection error'}
            </div>
          )}
        </div>
      )}

      {/* Tooltip for additional info */}
      <Tooltip
        isOpen={showTooltip}
        target="socket-status-badge"
        placement="bottom"
        toggle={() => setShowTooltip(!showTooltip)}
      >
        {getTooltipContent()}
      </Tooltip>
    </div>
  );
};

export default SocketConnectionStatus; 